/**
 * Documentation written by: ChatGPT - Henrik
 * This file provides utility functions for managing and verifying environment variables
 * in a Vite-based application. It uses an `enum` to define the required environment keys
 * and includes functions to access these variables and ensure they are properly set.
 *
 * - `EnvKeys`: Enum defining the required environment keys.
 * - `getEnv`: Utility function to retrieve environment variable values by their `EnvKeys` key.
 * - `verifyEnvVariables`: Verifies that all required environment variables are present
 *   and throws an error if any are missing.
 *
 * Example Usage:
 * - `getEnv('VITE_BASE_API_URL')`: Retrieves the value of the `VITE_BASE_API_URL` environment variable.
 * - Automatically verifies required variables at runtime via `verifyEnvVariables`.
 */

enum EnvKeys {
	VITE_BASE_API_URL,
}

// Utility function to get environment variables by enum key
const getEnv = (key: keyof typeof EnvKeys): string => {
	const envKey = EnvKeys[key]; // Numeric value
	const envName = EnvKeys[envKey]; // String name
	const value = import.meta.env[envName];

	if (!value) {
		throw new Error(`Missing required environment variable: ${envName}`);
	}

	return value;
};

export const verifyEnvVariables = (): void => {
	// Get the string keys from the EnvKeys enum
	const keys = Object.keys(EnvKeys).filter((key) =>
		isNaN(Number(key))
	) as (keyof typeof EnvKeys)[];

	// Iterate over the keys and check their corresponding values in `process.env`
	const missingKeys = keys.filter((key) => !import.meta.env[key]);

	// Throw an error if any environment variables are missing
	if (missingKeys.length > 0) {
		throw new Error(
			`Missing required environment variables: ${missingKeys.join(", ")}`
		);
	}
};

verifyEnvVariables();

export default {
	get: getEnv,
};
